import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, MonitorPlay, Users, Network, Clock, ShieldCheck, CalendarCheck,
  FileBarChart, Bell, ScrollText, Settings, LogOut, Menu, X, UserCircle, TriangleAlert, Timer,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const NAV = {
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { to: "/monitor", label: "Live Monitor", icon: MonitorPlay },
    { to: "/employees", label: "Employees", icon: Users },
    { to: "/processes", label: "Processes & Shifts", icon: Network },
    { to: "/policies", label: "Break Policies", icon: Clock },
    { to: "/attendance", label: "Attendance", icon: CalendarCheck },
    { to: "/reports", label: "Reports", icon: FileBarChart },
    { to: "/notifications", label: "Notifications", icon: Bell },
    { to: "/audit", label: "Audit Logs", icon: ScrollText },
    { to: "/corrections", label: "Corrections", icon: ShieldCheck },
  ],
  manager: [
    { to: "/manager", label: "Dashboard", icon: LayoutDashboard },
    { to: "/monitor", label: "Live Monitor", icon: MonitorPlay },
    { to: "/employees", label: "My Team", icon: Users },
    { to: "/attendance", label: "Attendance", icon: CalendarCheck },
    { to: "/exceptions", label: "Break Exceptions", icon: TriangleAlert },
    { to: "/reports", label: "Reports", icon: FileBarChart },
    { to: "/corrections", label: "Corrections", icon: ShieldCheck },
    { to: "/notifications", label: "Notifications", icon: Bell },
  ],
  employee: [
    { to: "/me", label: "My Dashboard", icon: LayoutDashboard },
    { to: "/my-attendance", label: "My Attendance", icon: CalendarCheck },
    { to: "/my-breaks", label: "My Breaks", icon: Timer },
    { to: "/corrections", label: "Corrections", icon: ShieldCheck },
    { to: "/profile", label: "Profile", icon: UserCircle },
  ],
};

function navKey(role) {
  if (role === "admin") return "admin";
  if (["manager", "senior_manager", "team_lead"].includes(role)) return "manager";
  return "employee";
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [clock, setClock] = useState(new Date());
  const [notifCount, setNotifCount] = useState(0);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => setNotifCount(r.data.filter((n) => !n.read).length)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [loc.pathname]);

  if (!user) return null;
  const items = NAV[navKey(user.role)];
  const roleLabel = user.role.replace("_", " ").toUpperCase();

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-5 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#08A7A0] font-display text-lg font-extrabold text-white">G</div>
        <div className="leading-tight">
          <p className="font-display text-sm font-extrabold text-white">GoSource</p>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-teal-300">Workforce Control</p>
        </div>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-[#08A7A0] text-white shadow-sm" : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <it.icon className="h-[18px] w-[18px]" />
            {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3">
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 transition-colors hover:bg-red-500/20 hover:text-red-300"
        >
          <LogOut className="h-[18px] w-[18px]" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F7FA]">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 bg-[#0B1F33] lg:block">{SidebarInner}</aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[#0B1F33]">{SidebarInner}</aside>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/90 px-4 backdrop-blur-md sm:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle">
              <Menu className="h-6 w-6 text-[#0B1F33]" />
            </button>
            <div className="hidden items-center gap-2 rounded-full bg-teal-50 px-3 py-1 sm:flex">
              <span className="h-2 w-2 rounded-full bg-[#08A7A0] gs-live-dot" />
              <span className="font-mono text-xs font-semibold text-teal-700">{clock.toLocaleTimeString("en-IN", { hour12: false })}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="topbar-notifications" onClick={() => nav("/notifications")} className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100">
              <Bell className="h-5 w-5" />
              {notifCount > 0 && (
                <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {notifCount}
                </span>
              )}
            </button>
            <div className="flex items-center gap-2.5 rounded-full border border-slate-200 bg-white py-1 pl-1 pr-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0B1F33] text-sm font-bold text-white">
                {user.name?.[0] || "U"}
              </div>
              <div className="hidden leading-tight sm:block">
                <p className="text-xs font-bold text-[#0B1F33]">{user.name}</p>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-[#08A7A0]">{roleLabel}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
